import { kolomParameters, type Facet, type FilterStand, type Filterwaarde, type Kolom, type Lijst } from '@xxtract/tabelfilters';
import type { FilterVelden } from '@/components/feedback/FilterRejectedMessage';
import { SERVICE_STATUSES } from '../orders/orders.api.constants';
import { stepLabel } from './format';
import type { KpiState, PhysicalItem, StepItemRow } from './processes.api';

/**
 * spec-tabelfilters story 8: de kolommen van de drie Processen-lijsten. Sleutels, parameters en
 * standaardsortering zijn gelijk aan `STAP_ITEMS_LIJST` en `PROCES_ITEMS_LIJST` in
 * `apps/api/src/modules/processes/processes.service.ts`; alleen de kopsleutels volgen de web.
 */

const KPI_STATEN: readonly KpiState[] = ['on_time', 'over_kpi', 'stuck', 'not_started'];
export const TOESTANDEN = ['stagnating', 'notReceived', 'onTrack'] as const;
export type Toestand = (typeof TOESTANDEN)[number];

/** Gedeelde Processen-paginafilter; een wijziging zet de lijsten op pagina 1 (§7, story 12). */
export const PROCESSEN_PAGINAFILTER = 'processen';

/**
 * Story 3.1 (FR9, AD-20, NFR4): de gedeelde Processen-filters. `gln` is een queryfilter (AD-20, geen
 * tenant-scope), nooit een persoonsgegeven (NFR4). Sinds spec-tabelfilters story 12 afgeleid uit de
 * lijststand `processen`, zodat API-functies en sleutels van queries ongewijzigd blijven.
 */
export interface ProcessFilterValues {
  gln?: string;
  from?: string;
  to?: string;
  status?: string;
  /** Werkdagen; leeg = de standaard van de bron (Story 2.1 `processKpiConfig.stuckThresholdWorkdays`). */
  stilstandDrempelWerkdagen?: number;
}

/** De drempel die het veld toont zolang er niets gekozen is (weergave, geen opslag). */
export const DEFAULT_STILSTAND_DREMPEL_WERKDAGEN = 5;

/** De velden van de filterbalk, met hun label — ook de sleutels die de API in `details.issues[].path` noemt. */
export const PROCESS_FILTER_VELDEN = {
  gln: 'processes.filters.klant',
  status: 'processes.filters.status',
  from: 'processes.filters.from',
  to: 'processes.filters.to',
  stilstandDrempelWerkdagen: 'processes.filters.stilstandDrempel',
} as const;

/**
 * De Processen-paginafilter als lijst (story 12, CAP-13): leest precies de oude parameternamen, zodat oude
 * links startfilter worden. De drempel is tekst zonder eigen toets: de API toetst 1..90 en weigert met 400.
 */
export const PROCESSEN_FILTER_LIJST: Lijst<unknown> = {
  alleenKolomfilters: true,
  kolommen: [
    { sleutel: 'gln', kopSleutel: 'processes.filters.klant', type: 'tekst', bron: 'klantenlijst', alleenKeuze: true, minTekens: 2, waarde: () => null },
    {
      sleutel: 'status',
      kopSleutel: 'processes.filters.status',
      type: 'keuzelijst',
      enkelvoudig: true,
      vasteWaarden: SERVICE_STATUSES,
      waardeLabel: (w, t) => t(`orders.serviceStatus.${w}`),
      waarde: () => null,
    },
    { sleutel: 'periode', kopSleutel: 'handlingTime.periode', type: 'datum', urlVan: 'from', urlTot: 'to', waarde: () => null },
    { sleutel: 'stilstandDrempelWerkdagen', kopSleutel: 'processes.filters.stilstandDrempel', type: 'tekst', waarde: () => null },
  ],
};

const tekst = (w: Filterwaarde | undefined) => (typeof w === 'string' && w.trim() !== '' ? w.trim() : undefined);

/** Lijststand → filterwaarden: getrimd, drempel als getal, lege velden en `_leeg` weg. */
export function processenFilterWaarden(filters: FilterStand | undefined): ProcessFilterValues {
  const uit: ProcessFilterValues = {};
  const gln = tekst(filters?.gln);
  if (gln) uit.gln = gln;
  const status = filters?.status;
  const gekozen = Array.isArray(status) ? status.find((w) => w !== null) : undefined;
  if (gekozen) uit.status = gekozen;
  const periode = filters?.periode;
  if (periode && typeof periode === 'object' && !Array.isArray(periode)) {
    if (periode.van) uit.from = periode.van;
    if (periode.tot) uit.to = periode.tot;
  }
  const drempel = tekst(filters?.stilstandDrempelWerkdagen);
  if (drempel !== undefined) uit.stilstandDrempelWerkdagen = Number(drempel);
  return uit;
}

/** Query-string bouwer voor `processes.api.ts` — laat lege velden weg (geen `?gln=`-ruis). */
export function toProcessFiltersQuery(filters: ProcessFilterValues): string {
  const params = new URLSearchParams();
  if (filters.gln != null && filters.gln !== '') params.set('gln', filters.gln);
  if (filters.from != null && filters.from !== '') params.set('from', filters.from);
  if (filters.to != null && filters.to !== '') params.set('to', filters.to);
  if (filters.status != null && filters.status !== '') params.set('status', filters.status);
  if (filters.stilstandDrempelWerkdagen != null) params.set('stilstandDrempelWerkdagen', String(filters.stilstandDrempelWerkdagen));
  return params.toString();
}

/** Story 4 (spec-processen-klantfilter): a filter the step-timing and service-list blocks do not reach. The threshold alone does not narrow the population. */
export function hasTimingOrListUnreachableFilter(filters: ProcessFilterValues): boolean {
  return Boolean(filters.gln || hasPeriodOrStatusFilter(filters));
}

/** Story 4: a filter the physical counts do not reach (they follow customer and threshold only). */
export function hasPeriodOrStatusFilter(filters: ProcessFilterValues): boolean {
  return Boolean(filters.from || filters.to || filters.status);
}

export const stapItemsSleutel = (name: string, step: string) => `stapItems:${name}:${step}`;
export const FYSIEK_IN_HUIS_SLEUTEL = 'fysiekInHuis';
export const procesItemsSleutel = (name: string) => `procesItems:${name}`;

/** Serverstandaard per endpoint, zodat de pagina tegen het totaal begrensd kan worden. */
export const STAP_ITEMS_PAGINAGROOTTE = 20;
export const PROCES_ITEMS_PAGINAGROOTTE = 25;

export const STAP_ITEMS_LIJST: Lijst<StepItemRow> = {
  kolommen: [
    { sleutel: 'product', kopSleutel: 'processes.stepDetail.columns.product', type: 'tekst', waarde: (r) => r.gtin },
    { sleutel: 'omschrijving', kopSleutel: 'processes.stepDetail.columns.description', type: 'tekst', waarde: (r) => r.description },
    { sleutel: 'dienst', kopSleutel: 'processes.stepDetail.columns.service', type: 'keuzelijst', waarde: (r) => r.service },
    { sleutel: 'klant', kopSleutel: 'processes.stepDetail.columns.client', type: 'keuzelijst', naamOpzoeker: true, waarde: (r) => r.gln },
    { sleutel: 'order', kopSleutel: 'processes.stepDetail.columns.order', type: 'tekst', waarde: (r) => r.orderNumber },
    { sleutel: 'sinds', kopSleutel: 'processes.stepDetail.columns.since', type: 'datum', waarde: (r) => r.receivedAt },
    { sleutel: 'werkdagen', kopSleutel: 'processes.stepDetail.columns.workdays', type: 'getal', eersteRichting: 'af', waarde: (r) => r.workdaysSinceReceipt },
    {
      sleutel: 'normstatus',
      kopSleutel: 'processes.stepDetail.columns.kpiState',
      type: 'keuzelijst',
      vasteWaarden: KPI_STATEN,
      waardeLabel: (w, t) => t(`processes.kpi.state.${w}`),
      waarde: (r) => r.kpiState,
    },
  ],
  standaardSortering: () => [{ sleutel: 'werkdagen', richting: 'af' }],
};

/**
 * Het fysieke stap-pad is extern gepagineerd (CAP-10, besluit Friso): alleen één klant uit de
 * volledige klantenlijst, niets sorteerbaar, dus ook geen standaardsortering op de koppen.
 */
export const STAP_ITEMS_FYSIEK_LIJST: Lijst<StepItemRow> = {
  kolommen: STAP_ITEMS_LIJST.kolommen.map((k): Kolom<StepItemRow> =>
    k.sleutel === 'klant'
      ? { ...k, naamOpzoeker: undefined, enkelvoudig: true, bron: 'klantenlijst', sorteer: false }
      : { ...k, filter: false, sorteer: false },
  ),
};

function toestand(r: PhysicalItem): Toestand {
  return r.stagnating ? 'stagnating' : r.notReceived ? 'notReceived' : 'onTrack';
}

/** Producten: bij een dienst betekent een niet-gestarte klok "nog niet op te pakken", niet "nog niet ontvangen". */
function productenLijst(dienst: boolean): Lijst<PhysicalItem> {
  return {
    kolommen: [
      { sleutel: 'product', kopSleutel: 'processes.physicalItems.columns.product', type: 'tekst', waarde: (r) => r.gtin },
      { sleutel: 'klant', kopSleutel: 'processes.physicalItems.columns.client', type: 'keuzelijst', naamOpzoeker: true, waarde: (r) => r.gln },
      {
        sleutel: 'stap',
        kopSleutel: 'processes.physicalItems.columns.step',
        type: 'keuzelijst',
        waardeLabel: (w, t) => stepLabel(w, t as Parameters<typeof stepLabel>[1]),
        waarde: (r) => r.canonicalStep,
      },
      {
        sleutel: 'toestand',
        kopSleutel: 'processes.physicalItems.columns.status',
        type: 'keuzelijst',
        vasteWaarden: TOESTANDEN,
        waardeLabel: (w, t) => t(`processes.physicalItems.status.${dienst && w === 'notReceived' ? 'notPickable' : w}`),
        waarde: toestand,
      },
      { sleutel: 'sinds', kopSleutel: 'processes.stepDetail.columns.since', type: 'datum', waarde: (r) => r.since },
      // Kop "In deze stap": de werkdagen die het product al in zijn huidige stap staat.
      { sleutel: 'werkdagen', kopSleutel: 'processes.physicalItems.columns.since', type: 'getal', eersteRichting: 'af', waarde: (r) => r.workdaysInStep },
    ],
    standaardSortering: () => [{ sleutel: 'werkdagen', richting: 'af' }],
  };
}

export const FYSIEKE_ITEMS_LIJST = productenLijst(false);
export const DIENST_ITEMS_LIJST = productenLijst(true);

/**
 * De ene leeslijst van "in huis" (§7): de stapladder linkt met `?step=` (`ProcesDetail.tsx`), dat
 * wordt de stapkolom. Verder gelijk aan de tabel, zodat ook `page`, `sort` en de andere kolommen
 * gelezen worden.
 */
export const FYSIEK_IN_HUIS_START: Lijst<PhysicalItem> = {
  ...FYSIEKE_ITEMS_LIJST,
  kolommen: FYSIEKE_ITEMS_LIJST.kolommen.map((k) => (k.sleutel === 'stap' ? { ...k, urlNaam: 'step' } : k)),
};

/** Paginafilter- en kolomvelden, zodat een 400 op een kolomparameter als geweigerd filter herkend wordt. */
export function procesFilterVelden<R>(lijst: Lijst<R>): FilterVelden {
  const kop = (sleutel: string) => lijst.kolommen.find((k) => k.sleutel === sleutel)!.kopSleutel;
  return {
    ...PROCESS_FILTER_VELDEN,
    ...Object.fromEntries(kolomParameters(lijst).map((p) => [p.parameter, kop(p.sleutel)])),
    sort: 'tabel.sorteerOp',
  };
}

/**
 * De eventcodes van de fysieke keten op volgorde, zonder aanmelding (zoals `chain` in het antwoord).
 * Spiegelt `PHYSICAL_CHAIN` in `apps/api/src/shared/contracts/process-orders.contract.ts`.
 */
const FYSIEKE_KETEN = ['RECEIVED', 'MEASURED', 'PHOTOGRAPHED', 'PACKAGED', 'RETURNED'];

/** Plek van een stap in de fysieke keten; -1 buiten de keten (bijv. `ONHOLD/SET`). */
export function fysiekeKetenPlek(stap: string): number {
  const [proces, code] = stap.toUpperCase().split('/');
  return proces === 'PHYSICAL' ? FYSIEKE_KETEN.indexOf(code ?? '') : -1;
}

/** Plek van een stap op de ladder van een proces (volgorde van de bron); -1 als hij er niet op staat. */
export function ladderPlek(ladder: readonly { canonicalStep: string }[]): (stap: string) => number {
  const plekken = new Map(ladder.map((s, i) => [s.canonicalStep.toUpperCase(), i]));
  return (stap) => plekken.get(stap.toUpperCase()) ?? -1;
}

/** De stapcode voor werk zonder gemelde stap; de bron zet hem als laatste sport op de ladder. */
const ONBEKENDE_STAP = 'UNKNOWN';

/**
 * Stapkeuzes in procesvolgorde in plaats van op label (besluit Friso 27-09-2026): gekozen eerst (§3),
 * dan op plek; daarna stappen zonder plek (in serverorde), "Stap onbekend" en `(leeg)`.
 */
export function stapkeuzesOpProcesvolgorde(keuzes: Record<string, Facet[]>, filters: FilterStand, plek: (stap: string) => number): Record<string, Facet[]> {
  const stap = keuzes.stap;
  if (!stap) return keuzes;
  const eigen = filters.stap;
  const gekozen = new Set(Array.isArray(eigen) ? eigen : []);
  // Achteraan: stappen zonder plek, dan "Stap onbekend" (ook als de ladder hem als laatste sport draagt), dan (leeg).
  const rang = (f: Facet) => {
    if (f.waarde === null) return Number.MAX_SAFE_INTEGER;
    if (f.waarde.toUpperCase() === ONBEKENDE_STAP) return Number.MAX_SAFE_INTEGER - 1;
    const p = plek(f.waarde);
    return p < 0 ? Number.MAX_SAFE_INTEGER - 2 : p;
  };
  const geordend = [...stap].sort((a, b) => Number(gekozen.has(b.waarde)) - Number(gekozen.has(a.waarde)) || rang(a) - rang(b));
  return { ...keuzes, stap: geordend };
}
